import type { CSSProperties, FC, ReactNode } from 'react'
import type { ContentBlock } from '@/types/content'

import { cn } from '@/lib/utils'
import { cms, hasValue, isPlaceholder } from '@/lib/cms'
import { reveal } from '@/lib/reveal'

type IntroSize = '2xl' | 'xl' | 'lg' | 'md'

// reveal: fades up when scrolled into view. entrance: plays once on load with pure CSS (use above the fold).
type IntroMotion = 'reveal' | 'entrance' | 'none'

interface SectionIntroProps {
    block?: ContentBlock | null
    as?: 'h1' | 'h2'
    size?: IntroSize
    tone?: 'light' | 'dark'
    headingId?: string
    showText?: boolean
    motion?: IntroMotion
    className?: string
    headingClassName?: string
    textClassName?: string
}

const headingSizes: Record<IntroSize, string> = {
    '2xl': 'text-display-2xl',
    xl: 'text-display-xl',
    lg: 'text-display-lg',
    md: 'text-display-md'
}

interface EyebrowProps {
    children: ReactNode
    tone?: 'light' | 'dark'
    className?: string
}

// Mono label with a short rule in front, used above every section headline.
export const Eyebrow: FC<EyebrowProps> = ({ children, tone = 'light', className }): ReactNode => {
    return (
        <p className={cn('type-eyebrow flex items-center gap-3', tone === 'dark' ? 'text-ember-bright' : 'text-ember', className)}>
            <span aria-hidden='true' className='h-px w-8 bg-current' />
            {children}
        </p>
    )
}

const ENTRANCE = 'animate-in fade-in slide-in-from-bottom-3 fill-mode-both duration-1000 ease-brand'

const motionProps = (motion: IntroMotion, delay: number): { className?: string, style?: CSSProperties, 'data-reveal'?: string } => {
    if (motion === 'reveal') return reveal(delay)
    if (motion === 'entrance') return { className: ENTRANCE, style: { animationDelay: `${delay}ms` } }

    return {}
}

// Eyebrow, display headline and lead text of a content block, each inline-editable.
export const SectionIntro: FC<SectionIntroProps> = ({
    block,
    as: Heading = 'h2',
    size = 'lg',
    tone = 'light',
    headingId,
    showText = true,
    motion = 'reveal',
    className,
    headingClassName,
    textClassName
}): ReactNode => {
    if (!block) return null

    const eyebrowMotion = motionProps(motion, 0)
    const headingMotion = motionProps(motion, 90)
    const textMotion = motionProps(motion, 180)

    return (
        <div className={cn('flex flex-col gap-6', className)}>
            {hasValue(block.dachzeile) && (
                <div {...eyebrowMotion}>
                    <Eyebrow tone={tone}>
                        <span data-mdlfy-cms={cms('inhalte', block.id, 'dachzeile')}>{block.dachzeile}</span>
                    </Eyebrow>
                </div>
            )}

            {hasValue(block.ueberschrift) && (
                <Heading
                    id={headingId}
                    data-mdlfy-cms={cms('inhalte', block.id, 'ueberschrift')}
                    data-reveal={headingMotion['data-reveal']}
                    style={headingMotion.style}
                    className={cn(
                        'type-display whitespace-pre-line',
                        headingSizes[size],
                        isPlaceholder(block.ueberschrift) && 'is-placeholder',
                        headingMotion.className,
                        headingClassName
                    )}
                >
                    {block.ueberschrift}
                </Heading>
            )}

            {showText && hasValue(block.text) && (
                <p
                    data-mdlfy-cms={cms('inhalte', block.id, 'text')}
                    data-reveal={textMotion['data-reveal']}
                    style={textMotion.style}
                    className={cn(
                        'type-lead max-w-xl whitespace-pre-line',
                        tone === 'dark' ? 'text-ash' : 'text-smoke',
                        isPlaceholder(block.text) && 'is-placeholder',
                        textMotion.className,
                        textClassName
                    )}
                >
                    {block.text}
                </p>
            )}
        </div>
    )
}
